import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

import { PAGES } from '../../../constants/pages';
import { BundleFormData } from '../constants/types';

import {
    BoxState,
    useBundleUpdatedSubscription,
    useUpdateBundleMutation,
    useGetBundleQuery,
    TotadminPermission,
} from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { GeneralInfoSection } from './GeneralInfoSection';
import { StatesSection } from './StatesSection';
import { UpdateStockConfirmationModal } from 'components/UpdateStockConfirmationModal';
import { BoxesContentForm } from '../components/BoxesContentForm';
import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';

type ParamTypes = {
    bundleId: string;
};

export const BundleDetails = () => {
    const { bundleId = '' } = useParams<ParamTypes>();
    const methods = useForm<BundleFormData>();
    const {
        formState: { isDirty },
        handleSubmit,
        reset,
    } = methods;
    const [updateBundle, { loading: updateBundleLoading }] = useUpdateBundleMutation();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    const {
        loading: bundleLoading,
        data: bundleData,
        error: bundleError,
    } = useGetBundleQuery({
        variables: { bundleId },
    });

    useEffect(() => {
        if (bundleData) {
            methods.reset(bundleData.bundle);
        }
    }, [bundleData, methods]);

    useBundleUpdatedSubscription();

    if (bundleLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (!bundleData || bundleError) {
        throw new Error('Une erreur est survenue lors de la récupération du bundle');
    }

    if (!bundleData.bundle) {
        return (
            <Container>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.bundles} />
                    </HeaderTitle>
                    <CTAsContainer>
                        <Link to="/bundles">
                            <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                        </Link>
                    </CTAsContainer>
                </Header>
                <div style={{ color: 'red', textAlign: 'center', marginTop: 50 }}>Bundle introuvable</div>
            </Container>
        );
    }

    const hasQuantityPickedChanges = (): boolean => {
        if (!bundleData?.bundle) return false;

        const initialBoxes = bundleData.bundle.boxes || [];
        const formBoxes = methods.getValues().boxes || [];

        // Fonction pour créer une map avec boxId-productId comme clé et quantityPicked comme valeur (en excluant 0 et null)
        const getQuantityPickedMap = (
            boxes: Array<{
                _id: string;
                products?: Array<{ _id: string; quantityPicked?: number | null }> | null;
            }>,
        ): Map<string, number> => {
            const quantityMap = new Map<string, number>();

            for (const box of boxes) {
                for (const product of box.products || []) {
                    const quantityPicked = product.quantityPicked;
                    // Exclure null, undefined et 0
                    if (quantityPicked !== null && quantityPicked !== undefined && quantityPicked !== 0) {
                        const key = `${box._id}-${product._id}`;
                        quantityMap.set(key, quantityPicked);
                    }
                }
            }

            return quantityMap;
        };

        // Boxes staged for removal (state Aborted) or bundle detachment (bundleId nulled) get their
        // quantities reset server-side → treat them as removed so the stock-update modal triggers.
        const initialBundleIdByBoxId = new Map(initialBoxes.map((box) => [box._id, box.bundleId]));
        const activeFormBoxes = formBoxes.filter(
            (box) =>
                box.state !== BoxState.Aborted &&
                !(box.bundleId == null && initialBundleIdByBoxId.get(box._id) != null),
        );

        const initialMap = getQuantityPickedMap(initialBoxes);
        const formMap = getQuantityPickedMap(activeFormBoxes);

        // Étape 1 : Comparer les clés avec un Set
        const initialKeys = new Set(initialMap.keys());
        const formKeys = new Set(formMap.keys());

        // Vérifier si les sets de clés sont différents
        if (!(initialKeys.size === formKeys.size && [...initialKeys].every((value) => formKeys.has(value)))) {
            return true;
        }

        // Étape 2 : Comparer les valeurs clé par clé
        for (const [key, initialValue] of initialMap.entries()) {
            const formValue = formMap.get(key);
            if (formValue === undefined || initialValue !== formValue) {
                return true;
            }
        }

        return false;
    };

    const handleFormSubmit = handleSubmit(async () => {
        if (hasQuantityPickedChanges()) {
            setIsConfirmationModalOpen(true);
        } else {
            // Aucune modification de quantityPicked, appeler directement avec shouldUpdateStocks=false
            await handleConfirm(false);
        }
    });

    const handleConfirm = async (shouldUpdateStocks: boolean) => {
        const bundleFormData = methods.getValues();

        try {
            // bundleId/state carry the staged actions: the API detaches the box from its bundle when
            // bundleId turns null, and removes it from the delivery when state turns Aborted.
            const formattedBoxes = bundleFormData.boxes.map((box) => ({
                _id: box._id,
                code: box.code,
                bundleId: box.bundleId ?? null,
                state: box.state,
                products: box.products.map((product) => ({
                    _id: product._id,
                    quantity: Number(product.quantity),
                    quantityPicked: product.quantityPicked !== null ? Number(product.quantityPicked) : null,
                    unitPrice: product.unitPrice !== null ? Number(product.unitPrice) : null,
                })),
            }));

            const { data } = await updateBundle({
                variables: {
                    bundle: {
                        _id: bundleId,
                        state: bundleFormData.state,
                        boxes: formattedBoxes,
                    },
                    shouldUpdateStocks,
                },
            });

            if (data?.updateBundle.bundle) {
                toast.success('Le bundle a bien été mis à jour');
                reset(data.updateBundle.bundle);
            }
        } catch (error) {
            toast.error('Une erreur est survenue lors de la mise à jour du bundle');
        }

        setIsConfirmationModalOpen(false);
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/bundles' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={handleFormSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.bundleDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={handleFormSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateBundleLoading}
                            saveTitle="Mettre à jour"
                            saveRequiredPermissions={[TotadminPermission.BundlesManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection bundleDetails={bundleData.bundle} />
                            <StatesSection bundleDetails={bundleData.bundle} />
                            <BoxesContentForm filterBy="position" />
                            {/* Les caisses avec le bundle, repérées par la même position que le formulaire
                                ci-dessus : leur contenu s'édite ici, sans rien écrire sur le bundle. */}
                            <AuditLogsSection
                                collectionDocumentIds={[
                                    bundleData.bundle._id,
                                    ...bundleData.bundle.boxes.map(({ _id }) => _id),
                                ]}
                                subjectLabels={Object.fromEntries(
                                    bundleData.bundle.boxes.map(({ _id, position }) => [_id, `#${position}`]),
                                )}
                            />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>

            <UpdateStockConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={() => {
                    setIsConfirmationModalOpen(false);
                }}
                onConfirm={(shouldUpdateStocks) => handleConfirm(shouldUpdateStocks)}
                isLoading={updateBundleLoading}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const CTAsContainer = styled.div`
    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
